import React, { useState, useEffect, useCallback } from "react";
import { API } from "aws-amplify";
import { listCandidates } from "../../graphql/queries";
import { updateCandidate, deleteCandidate } from "../../graphql/mutations";

const STATUS_OPTIONS = ["New", "Contacted", "Completed"];

export default function EstimateRequests() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      let allItems = [];
      let nextToken = null;
      do {
        const result = await API.graphql({
          query: listCandidates,
          variables: { limit: 200, nextToken },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        const page = result.data.listCandidates;
        allItems = allItems.concat(page.items.filter((item) => item !== null));
        nextToken = page.nextToken;
      } while (nextToken);
      allItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setCandidates(allItems);
    } catch (err) {
      console.error("Error fetching candidates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await API.graphql({
        query: updateCandidate,
        variables: { input: { id, status: newStatus } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      setCandidates((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: newStatus } : c))
      );
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.graphql({
        query: deleteCandidate,
        variables: { input: { id } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      setCandidates((prev) => prev.filter((c) => c.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Error deleting candidate:", err);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Contacted": return "status-contacted";
      case "Completed": return "status-completed";
      default: return "status-new";
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading estimates...</div>;
  }

  return (
    <div className="estimates-section">
      <div className="estimates-summary">
        <div className="summary-card">
          <span className="summary-number">{candidates.length}</span>
          <span className="summary-label">Total</span>
        </div>
        <div className="summary-card">
          <span className="summary-number">
            {candidates.filter((c) => !c.status || c.status === "New").length}
          </span>
          <span className="summary-label">New</span>
        </div>
        <div className="summary-card">
          <span className="summary-number">
            {candidates.filter((c) => c.status === "Contacted").length}
          </span>
          <span className="summary-label">Contacted</span>
        </div>
        <div className="summary-card">
          <span className="summary-number">
            {candidates.filter((c) => c.status === "Completed").length}
          </span>
          <span className="summary-label">Completed</span>
        </div>
      </div>

      {candidates.length === 0 ? (
        <div className="estimates-empty">No estimate requests yet.</div>
      ) : (
        <div className="estimates-table-wrapper">
          <table className="estimates-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>City</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <React.Fragment key={c.id}>
                  <tr
                    className={`estimate-row ${expandedId === c.id ? "expanded" : ""}`}
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  >
                    <td className="cell-name">
                      <svg className="expand-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                      {c.name}
                    </td>
                    <td>
                      <a
                        href={`mailto:${c.email}`}
                        className="cell-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.email}
                      </a>
                    </td>
                    <td>
                      <a
                        href={`tel:${c.phoneNumber}`}
                        className="cell-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.phoneNumber}
                      </a>
                    </td>
                    <td>{c.city}</td>
                    <td>{formatDate(c.createdAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className={`status-select ${getStatusClass(c.status || "New")}`}
                        value={c.status || "New"}
                        onChange={(e) => handleStatusChange(c.id, e.target.value)}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {deleteConfirm === c.id ? (
                        <div className="delete-confirm">
                          <button
                            className="btn-confirm-yes"
                            onClick={() => handleDelete(c.id)}
                          >
                            Yes
                          </button>
                          <button
                            className="btn-confirm-no"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn-delete"
                          onClick={() => setDeleteConfirm(c.id)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === c.id && (
                    <tr className="estimate-detail-row">
                      <td colSpan="7">
                        <div className="estimate-detail">
                          <div className="detail-grid">
                            <div className="detail-item">
                              <strong>Address:</strong>
                              <span>{c.address}</span>
                            </div>
                            <div className="detail-item">
                              <strong>Zip Code:</strong>
                              <span>{c.zipCode}</span>
                            </div>
                          </div>
                          <div className="detail-item full-width">
                            <strong>Project Description:</strong>
                            <p>{c.projectDescription}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
